import { useState, useEffect, useContext, useRef } from "react";
import { useSelector } from "react-redux";
import ExperienceContext from "./ExperienceContext";
import {
	TextInput,
	Switch,
	Button,
	Tabs,
	Input,
	Image,
	Modal,
	Group,
	Container,
	Loader,
} from "@mantine/core";
import {
	showSuccessNotification,
	showErrorNotification,
} from "../../utils/Notifications";
import uploadMedia from "../../API/uploadMedia";
import { CDN_URL } from "../../constant";

function UpdatePanoModal({
	isOpen,
	setIsUpdateModalOpen,
	closeControls,
	updateSceneDetails,
	setUpdateSceneDetails,
}) {
	const user = useSelector((state) => state.userInformationReducer.users);
	const [loading, setLoading] = useState(false);
	const { updatePanoramaDetails } = useContext(ExperienceContext);
	const fileInput = useRef(null);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [newUploadedFile, setNewUploadedFile] = useState(null);
	const [activeTab, setActiveTab] = useState("Upload");
	const [thumbUrl, setThumbUrl] = useState(null);
	const [text, setText] = useState("");
	const [isPrimary, setIsPrimary] = useState(true);

	useEffect(() => {
		setUploadedFile(updateSceneDetails?.url);
		setThumbUrl(updateSceneDetails?.thumbUrl);
		setText(updateSceneDetails?.name);
		setIsPrimary(updateSceneDetails?.isPrimary);
	}, [updateSceneDetails]);

	const handleFileUpload = (event) => {
		setNewUploadedFile(event.target.files[0]);
	};

	const handleUpdate = async () => {
		if (text === "") {
			showErrorNotification("Name cannot be empty");
			return;
		}
		if (!uploadedFile) {
			showErrorNotification("Please upload an image");
			return;
		}
		setLoading(true);

		if (newUploadedFile !== null) {
			const formData = new FormData();
			formData.append("myfile", newUploadedFile);
			formData.append(
				"bodyData",
				JSON.stringify({
					UserId: user?.UserId,
					AccountId: user?.AccountId,
					Name: text,
					AssetTypeId: 3,
				})
			);
			const result = await uploadMedia(formData);
			if (result.status == 201) {
				setLoading(false);
				showSuccessNotification("image uploaded");
				updatePanoramaDetails(
					updateSceneDetails.id,
					text,
					result.data.res.LocationUrl,
					result.data.res.PreviewUrl,
					isPrimary
				);
			} else {
				setLoading(false);
				showErrorNotification("Image not uploaded");
			}
		} else {
			updatePanoramaDetails(
				updateSceneDetails.id,
				text,
				uploadedFile,
				thumbUrl,
				isPrimary
			);
		}

		setIsUpdateModalOpen(false);
		setUploadedFile(null);
		setText("");
		setIsPrimary(true);
		setUpdateSceneDetails({});
		closeControls();
		setLoading(false);
	};

	return (
		<Modal
			centered
			size="lg"
			opened={isOpen}
			onClose={() => {
				setText("");
				setUploadedFile(null);
				setNewUploadedFile(null);
				setIsPrimary(true);
				setIsUpdateModalOpen(false);
				setUpdateSceneDetails({});
			}}
			title="Update Scene"
			styles={{
				modal: { backgroundColor: "#040F1F" },
				title: { color: "#ffffff", fontWeight: "700" },
			}}
		>
			{loading && (
				<div className="LoadingModal">
					<Loader className="LoadingModal_loader" />
				</div>
			)}
			<Container>
				<Switch
					my={15}
					labelPosition="left"
					label="Is this scene primary?"
					styles={{
						root: {
							flexDirection: "row-reverse",
							justifyContent: "flex-end",
						},
						input: {
							backgroundColor: "#040F1F",
							border: "1px solid #09254E",
						},
						label: {
							color: "#ffffff",
							fontWeight: "500",
							paddingRight: "12px",
							paddingLeft: "0px",
						},
					}}
					checked={isPrimary}
					onChange={(event) =>
						setIsPrimary(event.currentTarget.checked)
					}
				/>
				<TextInput
					my={5}
					label="Name"
					variant="filled"
					placeholder="Enter name"
					styles={{
						root: {
							display: "flex",
							alignItems: "center",
							gap: "10px",
						},
						input: {
							backgroundColor: "#040F1F",
							border: "1px solid #09254E",
						},
						label: {
							color: "#ffffff",
							fontWeight: "500",
							marginBottom: "0px",
						},
					}}
					value={text}
					onChange={(event) => setText(event.currentTarget.value)}
				/>
			</Container>
			<Tabs
				value={activeTab}
				onTabChange={setActiveTab}
				style={{ borderBottom: "none !important" }}
				styles={{
					tabsListWrapper: { borderBottom: "none !important" },
					tabsList: { borderBottom: "none !important" },
				}}
			>
				<Tabs.List>
					<Tabs.Tab value="Upload">Upload</Tabs.Tab>
					<Tabs.Tab value="Recent">Recent</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value="Upload" pt="xs">
					<Container style={{ width: "100%", minHeight: "300px" }}>
						<Container
							px={0}
							py={0}
							mx={0}
							my={0}
							style={{ position: "relative" }}
						>
							{newUploadedFile ? (
								<Image
									height={250}
									src={URL.createObjectURL(newUploadedFile)}
									fit="contain"
								/>
							) : (
								<Image
									height={250}
									src={CDN_URL + uploadedFile}
									fit="contain"
								/>
							)}
							<Button
								variant="filled"
								my={10}
								px={15}
								size="xs"
								radius={"md"}
								style={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
								}}
								sx={(theme) => ({
									color: theme.colors.white[0],
								})}
								onClick={() => {
									fileInput.current.click();
								}}
							>
								Change
							</Button>
							<Input
								id="file-input"
								ref={fileInput}
								type="file"
								accept=".jpg, .jpeg, .png"
								variant="unstyled"
								styles={{
									wrapper: {
										display: "none",
									},
								}}
								onChange={handleFileUpload}
							/>
						</Container>
					</Container>
				</Tabs.Panel>
				<Tabs.Panel value="Recent" pt="xs">
					Use recent panoramas
				</Tabs.Panel>
			</Tabs>
			<Group style={{ width: "100%", justifyContent: "end" }}>
				<Button
					variant="outline"
					my={10}
					px={30}
					size="xs"
					radius={"md"}
					sx={(theme) => ({
						color: theme.colors.white[0],
						borderColor: theme.colors.ivisit_light[0],
					})}
					onClick={() => {
						setText("");
						setUploadedFile(null);
						setIsUpdateModalOpen(false);
						setUpdateSceneDetails({});
					}}
				>
					Cancel
				</Button>
				<Button
					variant="outline"
					my={10}
					px={30}
					size="xs"
					radius={"md"}
					sx={(theme) => ({
						color: theme.colors.white[0],
						borderColor: theme.colors.ivisit_light[0],
					})}
					onClick={handleUpdate}
				>
					Update
				</Button>
			</Group>
		</Modal>
	);
}

export default UpdatePanoModal;
